import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';

const ClaimCodes: React.FC = () => {
  const navigate = useNavigate();
  const [showNewForm, setShowNewForm] = useState(false);
  const [batchCount, setBatchCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState('license_reclaim');

  const { data: claimCodesData, refetch, isLoading, error } = useQuery({
    queryKey: ['claim-codes-with-installs', selectedApp],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/claim-codes-with-installs?app=${selectedApp}`);
      return response;
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading claim codes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Error loading claim codes: {error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }

  const handleGenerateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await apiClient.post<any>('/api/claim-codes/batch', {
        count: batchCount,
        app: selectedApp,
      });
      if (result?.codes) {
        alert(`Successfully generated ${result.codes.length} claim codes!`);
        setBatchCount(10);
        setShowNewForm(false);
        // Refetch claim codes
        refetch();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate claim codes';
      alert(`Error: ${errorMessage}`);
      console.error('Failed to generate claim codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimCodes = claimCodesData?.items || [];
  const stats = claimCodesData;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Claim Codes</h1>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Generate Batch
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Total Codes</div>
          <div className="text-2xl font-bold text-gray-900">{stats?.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Active</div>
          <div className="text-2xl font-bold text-green-600">{stats?.active || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Consumed</div>
          <div className="text-2xl font-bold text-blue-600">{stats?.consumed || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Linked to Installs</div>
          <div className="text-2xl font-bold text-purple-600">{stats?.consumed_with_installs || 0}</div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Application</label>
        <select
          value={selectedApp}
          onChange={(e) => setSelectedApp(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="license_reclaim">LicenseReclaim</option>
          <option value="orbit">Orbit</option>
          <option value="doc_route">DocRoute</option>
        </select>
      </div>

      {showNewForm && (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">Generate Claim Codes</h2>
          <form onSubmit={handleGenerateBatch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Codes</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={batchCount}
                onChange={(e) => setBatchCount(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Generate between 1 and 1000 codes</p>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Generating...' : 'Generate'}
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Claim Code</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Tier / Seats</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Consumed Install</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Company</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
            </tr>
          </thead>
          <tbody>
            {claimCodes.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No claim codes yet. Generate a batch to get started.
                </td>
              </tr>
            ) : (
              claimCodes.map((code: any, idx: number) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/claim-codes/${code.code}`)}>
                  <td className="px-6 py-4 text-sm font-medium text-blue-600 font-mono underline">{code.code}</td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        code.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : code.status === 'consumed'
                            ? 'bg-blue-100 text-blue-800'
                            : code.status === 'revoked'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {code.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-700">
                    <div>{code.tier}</div>
                    <div className="text-gray-500">{code.seat_limit} seats</div>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500 font-mono">
                    {code.consumed_install_id ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/claim-codes/${code.code}`);
                        }}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {code.consumed_install_id.slice(0, 12)}...
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{code.company_name || '—'}</td>
                  <td className="px-6 py-4 text-xs text-gray-600 max-w-xs truncate">{code.notes || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClaimCodes;
